import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';

export type ToastTone = 'success' | 'danger' | 'info';

export interface ToastOptions {
  message: string;
  tone?: ToastTone;
  icon?: string;
  duration?: number | null;
}

export type DismissToast = () => void;

interface ToastApi {
  show: (options: ToastOptions) => DismissToast;
  success: (message: string) => DismissToast;
  error: (message: string) => DismissToast;
}

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  icon: string;
}

const DEFAULT_DURATION = 4500;

const DEFAULT_ICON: Record<ToastTone, string> = {
  success: 'pi-check-circle',
  danger: 'pi-times-circle',
  info: 'pi-info-circle',
};

const ToastContext = createContext<ToastApi | null>(null);

/**
 * The application's notifications: a short line in the top right saying what just happened. Every action that
 * changes something says so, because otherwise the only evidence is that the page looks slightly different.
 *
 * Announced through one polite live region rather than one per toast: a confirmation is not worth interrupting
 * whatever a screen reader was in the middle of saying.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);
  const timers = useRef<number[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const api = useMemo<ToastApi>(() => {
    const show = ({ message, tone = 'success', icon = DEFAULT_ICON[tone], duration = DEFAULT_DURATION }: ToastOptions) => {
      const id = (nextId.current += 1);
      setToasts((current) => [...current, { id, message, tone, icon }]);

      if (duration !== null) {
        timers.current.push(window.setTimeout(() => dismiss(id), duration));
      }

      return () => dismiss(id);
    };

    return {
      show,
      success: (message) => show({ message, tone: 'success' }),
      error: (message) => show({ message, tone: 'danger' }),
    };
  }, [dismiss]);

  // Nothing left pending on the way out, or a timer fires into a component that is gone.
  useEffect(() => () => timers.current.forEach((timer) => window.clearTimeout(timer)), []);

  return (
    <ToastContext.Provider value={api}>
      {children}

      <div className="toast-stack" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`app-toast ${toast.tone}`}>
            <i className={`pi ${toast.icon}`} aria-hidden="true" />
            <span className="app-toast__message">{toast.message}</span>
            <button
              type="button"
              className="app-toast__close"
              aria-label={t('common.close')}
              onClick={() => dismiss(toast.id)}
            >
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);

  if (api === null) {
    throw new Error('useToast must be used inside a <ToastProvider>.');
  }

  return api;
}
